import React, { useState } from 'react';
import { DataGrid, GridColumnMenuContainer, GridColumnMenuSortItem, GridColumnMenuFilterItem, getGridStringOperators } from '@mui/x-data-grid';
import { IconButton, Button, Avatar, Box, MenuItem, Select, Typography } from '@mui/material';
import UserDeleteConfirmDialog from './UserDeleteDialog.jsx';
import UserResetDialog from './UserResetDialog.jsx';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonIcon from '@mui/icons-material/Person';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useNavigate } from 'react-router-dom';
import UserEditDialog from './UserEditDialog.jsx';

function CustomColumnMenu(props) {
  const { hideMenu, colDef } = props;
  return (
    <GridColumnMenuContainer {...props}>
      <GridColumnMenuSortItem onClick={hideMenu} colDef={colDef} />
      <GridColumnMenuFilterItem onClick={hideMenu} colDef={colDef} />
    </GridColumnMenuContainer>
  );
}

const supportedStringFilterOperators = ['startsWith', 'equals']
  .map((operatorValue) => getGridStringOperators().find(({ value }) => value === operatorValue))
  .filter(Boolean);

const stringFilterOperators = /** @type {any} */ (supportedStringFilterOperators);

/**
 * @param {{
 *   rows?: any[],
 *   loading?: boolean,
 *   defaultPageSize?: number,
 *   pageSizeOptions?: number[],
 *   page?: number,
 *   pageSize?: number,
 *   rowCount?: number,
 *   paginationMeta?: any,
 *   hasMore?: boolean,
 *   filterModel?: import('@mui/x-data-grid').GridFilterModel,
 *   onFilterModelChange?: (model: import('@mui/x-data-grid').GridFilterModel)=>void,
 *   onPageChange?: (page:number)=>void,
 *   onPageSizeChange?: (size:number)=>void,
 *   onRowUpdate?: (user: any)=>void,
 *   onRowAdd?: (user: any)=>void,
 *   onRowDelete?: (user: any)=>void,
 *   onRowReset?: (user: any)=>void,    
 *   onRowView?: (user: any)=>void,
 *   readOnly?: boolean,
 *   embedded?: boolean,
 *   defaultHospitalId?: string,
 *   defaultHospitalName?: string,
 *   fixedHospital?: boolean
 * }} props
 */
export default function UsersDataGrid(props) {
  const {
    rows = [],
    loading = false,
    defaultPageSize = 10,
    pageSizeOptions = [10, 15, 25],
    page,
    pageSize,
    rowCount,
    paginationMeta,
    hasMore = false,
    filterModel,
    onFilterModelChange,
    readOnly = false,
    embedded = false,
    defaultHospitalId = '',
    defaultHospitalName = '',
    fixedHospital = false,
  } = props;
  const navigate = useNavigate();

  const [localRows, setLocalRows] = React.useState(rows || []);
  React.useEffect(() => setLocalRows(rows || []), [rows]);

  const safeRows = (localRows || []).map((r, i) => ({ id: r.uid ?? r.id ?? r.patientId ?? r.patient_id ?? `row-${i}`, ...r }));

/** @type {import('@mui/x-data-grid').GridColDef[]} */
  const columns = [
    {
      field: 'avatar',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        // temporary placeholder; image handling not implemented yet
        return (
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Avatar sx={{ width: 32, height: 32 }}>
              <PersonIcon fontSize="small" />
            </Avatar>
          </Box>
        );
      },
    },
    { field: 'firstName', headerName: 'First Name', flex: 1, minWidth: 140, filterOperators: stringFilterOperators },
    { field: 'lastName',  headerName: 'Last Name',  flex: 1, minWidth: 140, filterOperators: stringFilterOperators },
    { field: 'email',     headerName: 'Email',      flex: 1.5, minWidth: 200, filterOperators: stringFilterOperators },
    { field: 'totalUploads', headerName: 'Total Uploads', flex: 0.8, minWidth: 120, type: 'number', filterable: false, align: 'center', headerAlign: 'center' },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 140,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      /** @param {{row:any}} params */
      renderCell: (params) => {
        const user = params.row || {};
        return (
          <div style={{display: 'flex', gap: 8, alignItems: 'center', height: '100%', justifyContent: 'center'}}>
            {!readOnly && (
              <IconButton
                size="small"
                sx={{ width: 28, height: 28, p: 0.25 }}
                aria-label={`reset-${user.id}`}
                title={`Reset password for ${user.firstName || ''} ${user.lastName || ''}`}
                onClick={() => {
                  setResettingUser(user);
                  setResetOpen(true);
                }}
              >
                <VpnKeyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
            <IconButton
              size="small"
              sx={{ width: 28, height: 28, p: 0.25 }}
              aria-label={`view-${user.id}`}
              title={`View ${user.firstName || ''} ${user.lastName || ''}`}
              onClick={() => typeof props.onRowView === 'function' && props.onRowView(user)}
            >
              <VisibilityIcon sx={{ fontSize: 16 }} />
            </IconButton>
            {!readOnly && (
              <IconButton
                size="small"
                sx={{ width: 28, height: 28, p: 0.25 }}
                aria-label={`edit-${user.id}`}
                title={`Edit ${user.firstName || ''} ${user.lastName || ''}`}
                onClick={() => openEditDialog(user)}
              >
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
            {!readOnly && (
              <IconButton
                size="small"
                color="error"
                sx={{ width: 28, height: 28, p: 0.25 }}
                aria-label={`delete-${user.id}`}
                title={`Delete ${user.firstName || ''} ${user.lastName || ''}`}
                onClick={() => handleDeleteClick(user)}
              >
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </div>
        );
      }
    }
  ];

  const [editingUser, setEditingUser] = useState(/** @type {any|null} */ (null));
  const createDialogUser = React.useMemo(() => ({
    hospitalId: defaultHospitalId || '',
    hospitalName: defaultHospitalName || '',
  }), [defaultHospitalId, defaultHospitalName]);
  const dialogUser = editingUser || createDialogUser;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState(/** @type {any|null} */ (null));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resettingUser, setResettingUser] = useState(/** @type {any|null} */ (null));
  const [resetOpen, setResetOpen] = useState(false);

  // callback when reset confirmed
  const handleResetConfirm = (user) => {
    if (typeof props.onRowReset === 'function') {
      props.onRowReset(user);
    }
    setResetOpen(false);
  };


  const [dialogError, setDialogError] = useState(null);
  const [saving, setSaving] = useState(false);

  /** @param {any} user */
  const openEditDialog = (user) => {
    setEditingUser(user);
    setDialogError(null);
    setDialogOpen(true);
  };

  const openAddDialog = () => {
    setEditingUser(null);
    setDialogError(null);
    setDialogOpen(true);
  };

  const closeEditDialog = () => {
    setDialogOpen(false);
    setDialogError(null);
  };

  const handleEditDialogExited = () => {
    setEditingUser(null);
  };

  /** @param {any} user */
  const handleDeleteClick = (user) => {
    setDeletingUser(user);
    setConfirmOpen(true);
  };

  const handleCancelDelete = () => {
    setDeletingUser(null);
    setConfirmOpen(false);
  };

  const handleConfirmDelete = () => {
    if (!deletingUser) return;
    setLocalRows((prev) => prev.filter((r) => r.id !== deletingUser.id));
    if (typeof props.onRowDelete === 'function') props.onRowDelete(deletingUser);
    if (editingUser && editingUser.id === deletingUser.id) closeEditDialog();
    setDeletingUser(null);
    setConfirmOpen(false);
  };

  /** @param {any} updatedUser */
  const handleSave = (updatedUser) => {
    setDialogError(null);
    setSaving(true);
    const exists = updatedUser.id && localRows.some(r => r.id === updatedUser.id);
    let actionPromise;
    let previousRows;

    if (exists) {
      previousRows = localRows;
      setLocalRows((prev) => prev.map((r) => (r.id === updatedUser.id ? { ...r, ...updatedUser } : r)));
      if (typeof props.onRowUpdate === 'function') actionPromise = props.onRowUpdate(updatedUser);
    } else {
      // delay adding until after remote success to avoid ghost rows on failure
      if (typeof props.onRowAdd === 'function') {
        actionPromise = props.onRowAdd(updatedUser).then(() => {
          const id = updatedUser.id ?? updatedUser.uid ?? `user-${Date.now()}`;
          const newRow = { id, ...updatedUser };
          setLocalRows((prev) => [newRow, ...prev]);
          // after creating a new user, prompt to send reset email
          setResettingUser(updatedUser);
          setResetOpen(true);
        });
      }
    }
    if (actionPromise && actionPromise.then) {
      actionPromise
        .then(() => {
          closeEditDialog();
        })
        .catch((err) => {
          setDialogError(err);
          if (exists && previousRows) {
            setLocalRows(previousRows);
          }
        })
        .finally(() => setSaving(false));
    } else {
      setSaving(false);
      if (!exists) {
        const id = updatedUser.id ?? updatedUser.uid ?? `user-${Date.now()}`;
        const newRow = { id, ...updatedUser };
        setLocalRows((prev) => [newRow, ...prev]);
        // newly added locally -- ask about reset
        setResettingUser(updatedUser);
        setResetOpen(true);
      }
      closeEditDialog();
    }
  };

  return (
    <div
      style={embedded ? { width: '100%' } : {
        width: '90%',
        maxWidth: 1600,
        margin: '0 auto',
        paddingTop: '2.5vw',
        paddingBottom: '1.5vw',
      }}
    >
      <div style={{
        border: '1px solid #e0e0e0',
        borderRadius: 8,
        background: '#fff',
        overflow: 'hidden',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: embedded ? 'flex-end' : 'space-between',
          paddingTop: 12,
          paddingBottom: 8,
          paddingLeft: 12,
          paddingRight: 12,
        }}>
          {!embedded && <div style={{ fontWeight: 400, fontSize: 32 }}>Users</div>}
          <div>
            {!readOnly && (
              <Button
                variant="contained"
                size="small"
                startIcon={<PersonAddIcon />}
                onClick={openAddDialog}
              >
                Add User
              </Button>
            )}
          </div>
        </div>
        <DataGrid
          rows={safeRows}
          loading={loading}
          columns={columns}
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={onFilterModelChange}
          slots={{ columnMenu: CustomColumnMenu }}
          density="compact"
          rowHeight={85}
          hideFooter
          sx={{
            border: 'none',
            borderRadius: 0,
            width: '100%',
            maxWidth: '100%',
            mt: 0,
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'transparent',
            },
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: 'transparent',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 590,
              color: 'common.black',
              fontSize: 17,
            },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-row': {
              minHeight: 64,
              maxHeight: 64,
            },
          }}
          aria-label="Users table"
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 3,
            px: 2,
            py: 1,
            borderTop: '1px solid #e0e0e0',
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Rows per page
            </Typography>
            <Select
              id="user-page-size"
              size="small"
              value={pageSize ?? defaultPageSize}
              onChange={(event) => {
                if (typeof props.onPageSizeChange === 'function') {
                  props.onPageSizeChange(Number(event.target.value));
                }
              }}
              disabled={loading}
              MenuProps={{ disableScrollLock: true }}
              sx={{
                minWidth: 72,
                '& .MuiSelect-select': {
                  py: 0.75,
                  pr: 3,
                  fontSize: 14,
                },
              }}
            >
              {pageSizeOptions.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </Select>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 56, textAlign: 'center' }}>
            {(page ?? 0) + 1}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              size="small"
              onClick={() => typeof props.onPageChange === 'function' && props.onPageChange((page ?? 0) - 1)}
              disabled={loading || (page ?? 0) <= 0}
              aria-label="Previous page"
            >
              <KeyboardArrowLeftIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => typeof props.onPageChange === 'function' && props.onPageChange((page ?? 0) + 1)}
              disabled={loading || !hasMore}
              aria-label="Next page"
            >
              <KeyboardArrowRightIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </div>

      <UserEditDialog
        open={dialogOpen}
        onClose={closeEditDialog}
        onExited={handleEditDialogExited}
        user={dialogUser}
        mode={editingUser ? 'edit' : 'create'}
        onSave={handleSave}
        saving={saving}
        error={dialogError}
        onErrorClear={() => setDialogError(null)}
        fixedHospital={fixedHospital}
      />

      <UserDeleteConfirmDialog
        open={confirmOpen}
        user={deletingUser}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
      <UserResetDialog
        open={resetOpen}
        user={resettingUser}
        onClose={() => setResetOpen(false)}
        onConfirm={handleResetConfirm}
      />
    </div>
  );
}
